/**
 * Backend-proposed corrective actions. NOTHING executes from the chat: a
 * `navigate` action is a deep link, and a `rerun` opens a confirmation
 * dialog naming exactly what will be launched — the click on "Confirmer"
 * is the operator's final confirmation.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import ReplayIcon from "@mui/icons-material/Replay";
import { api, ApiError, type ProposedAction } from "../lib/api";
import { errorText } from "../lib/errors";
import { ID_SLICE_LEN } from "../lib/config";
import { useToast } from "../hooks/useToast";
import { useEditMode } from "../hooks/useCustomer";

export function ProposedActions({ actions }: { actions: ProposedAction[] }) {
  const { t } = useTranslation();
  const edit = useEditMode();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirming, setConfirming] = useState<ProposedAction | null>(null);
  const [launching, setLaunching] = useState(false);

  async function confirmRerun() {
    if (!confirming) return;
    setLaunching(true);
    try {
      let newJobId: string;
      if (confirming.kind === "retry_job" && confirming.job_id) {
        newJobId = (await api.retryJob(confirming.job_id)).job_id;
      } else if (confirming.kind === "rerun" && confirming.tool && confirming.artifact_id) {
        newJobId = (
          await api.runTool({ tool: confirming.tool, artifact_id: confirming.artifact_id })
        ).job_id;
      } else {
        return;
      }
      toast(t("auditActions.rerunStarted"), "success");
      setConfirming(null);
      void navigate(`/runs/${encodeURIComponent(newJobId)}`);
    } catch (err) {
      // pre-upgrade runs never recorded their input: fall back to the tool
      // page instead of guessing which file to reprocess
      const body = err instanceof ApiError ? err.body : undefined;
      const toolKey = (body as { tool?: string } | undefined)?.tool;
      if (err instanceof ApiError && err.status === 409 && toolKey) {
        toast(errorText(err), "warning");
        setConfirming(null);
        void navigate(`/tool?select=${encodeURIComponent(toolKey)}`);
      } else {
        toast(errorText(err), "error");
      }
    } finally {
      setLaunching(false);
    }
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {t("auditActions.heading")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {actions.map((action, i) => (
          <Tooltip key={i} title={action.rationale ?? ""}>
            <Button
              size="small"
              variant="outlined"
              startIcon={action.kind === "navigate" ? <LaunchIcon /> : <ReplayIcon />}
              onClick={() => {
                if (action.kind === "navigate" && action.to) void navigate(action.to);
                else setConfirming(action);
              }}
            >
              {t(action.label_key, action.label_key)}
            </Button>
          </Tooltip>
        ))}
      </Stack>

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogTitle>{t("auditActions.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirming?.kind === "retry_job"
              ? t("auditActions.confirmRetryBody", {
                  id: (confirming.job_id ?? "").slice(0, ID_SLICE_LEN),
                })
              : t("auditActions.confirmBody", {
                  tool: confirming?.tool ?? "",
                  file: (confirming?.artifact_id ?? "").slice(0, ID_SLICE_LEN),
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={launching}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmRerun()}
            disabled={launching || !edit.canEdit}
          >
            {launching ? t("auditActions.launching") : t("auditActions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
